import { ReactElement, useMemo, useState } from 'react';
import { blue, charcoal, moonlight, opacity, red } from '@/ui/styles';
import { UIMenuItemProps, UIText } from '@/ui';
import { ChartContainer } from './chart-container';
import {
    TasksAndAlertsHourlyActivityProvider,
    useTasksAndAlertsHourlyActivity
} from '../providers/tasks-and-alerts-hourly-activity-provider';
import { uuidv4 } from '@/common/util';
import { Tooltip } from 'react-tooltip';
import { useTimeSettings } from '@/common/hooks';
import { useAnalyticsTranslation } from '../hooks';

type ChartType = 'tasks' | 'alerts';

const GAP = 4;
const BORDER_RADIUS = 4;
const X_AXIS_HEIGHT = 30;
const Y_AXIS_WIDTH = 50;

function InternalHoursActiveGraph(props: {
    data: number[][] | null;
    dateString: string | null;
    rightMenuText: string;
    rightMenuItems: UIMenuItemProps[];
    type: ChartType;
    description: string;
    loading: boolean;
    error: boolean;
}): ReactElement {
    const {
        data,
        dateString,
        rightMenuText,
        rightMenuItems,
        type,
        description,
        loading,
        error
    } = props;
    const { t } = useAnalyticsTranslation();

    return (
        <ChartContainer
            title={t('components.hours_active_graph.title')}
            titleWeight="medium"
            subtitle={dateString || '...'}
            rightMenuText={rightMenuText}
            rightMenuItems={rightMenuItems}
            description={description}
            loading={loading}
            error={error}
        >
            {data && <Heatmap data={data} type={type} />}
        </ChartContainer>
    );
}
function Heatmap(props: { data: number[][]; type: ChartType }): ReactElement {
    const { data, type } = props;

    const max = useMemo(() => {
        const allHours = data.flat();
        return Math.max(...allHours);
    }, [data]);

    return (
        <div
            style={{
                display: 'flex',
                flex: 1,
                flexDirection: 'column',
                gap: GAP
            }}
        >
            <div
                style={{
                    display: 'flex',
                    flex: 1,
                    flexDirection: 'row',
                    gap: GAP
                }}
            >
                <YAxisLabels />
                {data.map((dayData, dayIndex) => (
                    <div
                        key={dayIndex}
                        style={{
                            display: 'flex',
                            flex: 1,
                            flexDirection: 'column',
                            gap: GAP
                        }}
                    >
                        {dayData.map((count, hourIndex) => (
                            <Cell
                                key={hourIndex}
                                count={count}
                                max={max}
                                type={type}
                            />
                        ))}
                    </div>
                ))}
            </div>

            <XAxisLabels />
        </div>
    );
}

type CellProps = {
    count: number;
    max: number;
    type: ChartType;
};
function Cell(props: CellProps): ReactElement {
    const { count, max, type } = props;
    const { t } = useAnalyticsTranslation();

    function interpolateOpacity(value: number): number {
        if (0 === max) return 0; // Avoid division by zero
        return value / max;
    }

    const id = useMemo(() => `tooltip-${uuidv4()}`, []);
    const anchorSelect = `#${id}`;

    const baseColor = useMemo(() => {
        return type === 'tasks' ? blue : red;
    }, [type]);

    const tooltipText = useMemo(() => {
        if (type === 'tasks') {
            return t('components.hours_active_graph.tasks_count', { count });
        } else if (type === 'alerts') {
            return t('components.hours_active_graph.alerts_count', { count });
        }
        return '';
    }, [count, type, t]);

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    position: 'relative',
                    flex: 1,
                    borderRadius: BORDER_RADIUS,
                    backgroundColor: moonlight,
                    overflow: 'hidden'
                }}
            >
                <a
                    id={id}
                    style={{
                        display: 'flex',
                        flex: 1,
                        backgroundColor:
                            baseColor + opacity(interpolateOpacity(count))
                    }}
                />
            </div>
            <Tooltip
                anchorSelect={anchorSelect}
                place={'top'}
                style={{
                    backgroundColor: charcoal,
                    borderRadius: 8,
                    zIndex: 2
                }}
            >
                <UIText type="white" variant="small">
                    {tooltipText}
                </UIText>
            </Tooltip>
        </>
    );
}

function YAxisLabels(): ReactElement {
    const { use24HourClock } = useTimeSettings();
    const { t } = useAnalyticsTranslation();

    const hoursOfDay = useMemo(() => {
        if (use24HourClock) {
            return Array.from({ length: 24 }, (_, i) =>
                t('shared.time.hour_24', {
                    hour: i.toString().padStart(2, '0')
                })
            );
        }
        return Array.from({ length: 24 }, (_, i) =>
            t('shared.time.hour_12', {
                hour: i % 12 === 0 ? 12 : i % 12,
                meridiem: i < 12 ? t('shared.time.am') : t('shared.time.pm')
            })
        );
    }, [use24HourClock, t]);

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                width: Y_AXIS_WIDTH,
                gap: GAP
            }}
        >
            {hoursOfDay.map((hour, index) => (
                <div
                    key={index}
                    style={{
                        display: 'flex',
                        flex: 1,
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        paddingRight: GAP * 2
                    }}
                >
                    <UIText variant="small" type="default2" textAlign="right">
                        {hour}
                    </UIText>
                </div>
            ))}
        </div>
    );
}

function XAxisLabels(): ReactElement {
    const { t } = useAnalyticsTranslation();

    const daysOfWeek = useMemo(
        () => [
            t('shared.days.monday.short'),
            t('shared.days.tuesday.short'),
            t('shared.days.wednesday.short'),
            t('shared.days.thursday.short'),
            t('shared.days.friday.short'),
            t('shared.days.saturday.short'),
            t('shared.days.sunday.short')
        ],
        [t]
    );

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                height: X_AXIS_HEIGHT,
                marginLeft: Y_AXIS_WIDTH + GAP,
                gap: GAP
            }}
        >
            {daysOfWeek.map((day, index) => (
                <div
                    key={index}
                    style={{
                        display: 'flex',
                        flex: 1,
                        justifyContent: 'center',
                        alignItems: 'flex-start',
                        paddingTop: GAP * 2
                    }}
                >
                    <UIText variant="small" type="default2" textAlign="center">
                        {day}
                    </UIText>
                </div>
            ))}
        </div>
    );
}

export function HoursActiveGraph(): ReactElement {
    return (
        <TasksAndAlertsHourlyActivityProvider>
            <TasksHoursActiveGraph />
        </TasksAndAlertsHourlyActivityProvider>
    );
}

function TasksHoursActiveGraph(): ReactElement {
    const { t } = useAnalyticsTranslation();
    const { tasks, alerts, status, dateString } =
        useTasksAndAlertsHourlyActivity();

    const [view, setView] = useState<ChartType>('tasks');

    const rightMenuItems: UIMenuItemProps[] = useMemo(() => {
        const items: UIMenuItemProps[] = [
            {
                text: t('shared.labels.tasks'),
                onClick: () => {
                    setView('tasks');
                },
                leftIconName: view === 'tasks' ? 'check' : undefined
            },
            {
                text: t('shared.labels.alerts'),
                onClick: () => {
                    setView('alerts');
                },
                leftIconName: view === 'alerts' ? 'check' : undefined
            }
        ];

        return items;
    }, [view, t]);

    const rightMenuText = useMemo(() => {
        return view === 'tasks'
            ? t('shared.labels.tasks')
            : t('shared.labels.alerts');
    }, [view, t]);

    const description = useMemo(() => {
        if (view === 'tasks') {
            return t('components.hours_active_graph.description_tasks');
        } else if (view === 'alerts') {
            return t('components.hours_active_graph.description_alerts');
        }
        return '';
    }, [view, t]);

    return (
        <InternalHoursActiveGraph
            data={view === 'tasks' ? tasks : view === 'alerts' ? alerts : null}
            dateString={dateString}
            type={view}
            rightMenuText={rightMenuText}
            rightMenuItems={rightMenuItems}
            description={description}
            loading={status === 'pending'}
            error={status === 'error'}
        />
    );
}
